import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/api/client";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  const refreshMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      localStorage.removeItem("avida_token");
      return null;
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth callback, skip the /me check — AuthCallback exchanges the session_id first.
    if (window.location.hash?.includes("session_id=")) {
      setChecking(false);
      return;
    }
    const token = localStorage.getItem("avida_token");
    if (!token) {
      setChecking(false);
      return;
    }
    refreshMe().finally(() => setChecking(false));
  }, [refreshMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("avida_token", data.token);
    setUser(data.user);
    try { sessionStorage.setItem("avida_briefing_pending", "1"); } catch {}
    const sec = data.security;
    if (sec?.is_new && !sec.is_first) {
      const kinds = [];
      if (sec.is_new_device) kinds.push("a new device");
      if (sec.is_new_location) kinds.push("a new location");
      toast.warning(`Sign-in from ${kinds.join(" and ")}`, {
        description: `${sec.device_label}${sec.location_label ? " · " + sec.location_label : ""}. If this wasn't you, change your password now.`,
        duration: 12000,
      });
    }
    return data.user;
  };

  const loginWithGoogleSession = async (sessionId) => {
    const { data } = await api.post("/auth/google/session", { session_id: sessionId });
    localStorage.setItem("avida_token", data.token);
    setUser(data.user);
    try { sessionStorage.setItem("avida_briefing_pending", "1"); } catch {}
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("avida_token");
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, checking, login, loginWithGoogleSession, logout, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
